import type { ImageCrop, Scene, SceneElement, Transform } from '@pz/scene';
import {
  FREE_ZOOM_MIN,
  coverPhotoFrame,
  mapLocationOf,
  mapViewportOf,
  mapZoomOffset,
  panPhotoCrop,
  photoCssFilter,
  photoPlacement,
  readPlacements,
  shapeDef,
  turnPhotoCrop,
  zoomPhotoCropAt,
} from '@pz/scene';
import type { BindingDef, Values } from './types.js';
import { pointerIsCoarse } from './layers.js';
import { openPhotoFramer, repeatWhileHeld } from './photo-framer.js';

/**
 * On-canvas photo editing, the way Customily does it: once a photo is uploaded,
 * the shopper adjusts it by touching the preview itself — drag to move it inside
 * its slot, pinch or scroll (or the +/− buttons) to zoom. No separate crop box.
 *
 * The editor floats an interactive overlay over each photo slot and writes the
 * resulting framing back into the form as `{assetId, crop}`; the preview then
 * re-renders through the normal pipeline.
 *
 * During the gesture itself the overlay shows the photo as a plain `<img>`
 * moved with CSS — GPU-composited, 60 fps. The print renderer is invoked once,
 * when the fingers lift. Rendering per pointermove was the single biggest
 * source of jank: every move burned a full WASM rasterisation on the main
 * thread, which stuttered the drag it was trying to display.
 */

interface Crop {
  cx: number;
  cy: number;
  zoom: number;
  /** "Straighten the picture" — degrees inside the fixed box. */
  rotation?: number;
  /**
   * A cut-out head placed on a Mini Me body: the window may leave the picture
   * and zoom below the cover fit (outside a cut-out is transparent), so the
   * shopper can make the head smaller or move it anywhere in its slot.
   */
  free?: boolean;
  /** The chosen head of a group cut-out; the others stay hidden. */
  keep?: { x: number; y: number; w: number; h: number };
}

export interface CanvasEditorOptions {
  /** The preview box the canvas sits in; overlays are positioned inside it. */
  box: HTMLElement;
  canvas: HTMLCanvasElement;
  /** Where an asset's pixels live, for the pan/zoom maths. */
  resolveUrl: (assetId: string) => string | undefined;
  /** Writes the shopper's framing back into the form. */
  onCrop: (optionId: string, value: unknown) => void;
  /**
   * A drag on a cut-out slot started (true) or ended (false). The picture is
   * transparent around the head, so the preview must stop drawing the old
   * head underneath the moving stand-in — two heads otherwise.
   */
  onDragging?: (optionId: string, dragging: boolean) => void;
  /**
   * Writes where the shopper dragged a movable layer: the whole placement map,
   * element id → offset in units of the layer's own size.
   */
  onPlace?: (placements: Record<string, { dx: number; dy: number }>) => void;
  /**
   * May this location question be answered by dragging the map on the preview?
   *
   * Off unless the caller says otherwise: Customily's shopper picks the place in
   * the field's dialog, and an overlay that swallows drags on top of the printed
   * design is not part of that flow. A template author turns it back on per
   * option with `previewPan`.
   */
  allowMapPan?: (optionId: string) => boolean;
  /** The question's name, shown in the tool bar when it holds several photos. */
  labelFor?: (optionId: string) => string;
  /**
   * Renders a scene of just the layers drawn OVER a photo (transparent
   * elsewhere) at the preview's resolution. While the shopper drags a photo,
   * that picture is laid over the moving stand-in, so a frame, a caption or a
   * Mini Me collar stays on top of it exactly as the print will have it.
   * Undefined (or a renderer that cannot) keeps the stand-in on top.
   */
  renderLayers?: (scene: Scene) => Promise<{ pixels: Uint8Array | Uint8ClampedArray; width: number; height: number } | undefined>;
  /**
   * Does this question answer with the shopper's own picture (an upload, an
   * AI image)? Only those are framed on the preview.
   *
   * A picture CHOICE — a map pin, a clipart swatch, a library dropdown — also
   * drives an image layer, and its answer ("Pin 1") is a string like a photo's
   * asset id. Without this the pin got the photo tools: "Prevuci sliku" and a
   * zoom that did nothing, over a picture the shopper never uploaded.
   * Undefined treats every image answer as a photo (the older behaviour).
   */
  isPhoto?: (optionId: string) => boolean;
}

interface Target {
  optionId: string;
  /** The layer the photo fills; what is drawn above it covers the stand-in. */
  elementId: string;
  assetId: string;
  crop: Crop;
  /** Slot box in viewport coordinates, before the layer's own turn. */
  rect: { x: number; y: number; w: number; h: number };
  aspect: number;
  /**
   * The layer's own rotation, skew and flips (about its centre), as the
   * renderer applies them. The overlay wears the same CSS transform, and a
   * finger's movement is carried back through its inverse, so a tilted or
   * mirrored slot drags the way it looks.
   */
  linear?: Linear;
  /** The layer's CSS transform, for the overlay; empty when it has none. */
  cssTransform: string;
  /** The layer's colour effects and opacity, as CSS, for the stand-in. */
  filter?: string;
  opacity?: number;
  /** The layer's clip shape (a heart, a circle) as a CSS mask image. */
  shapeMask?: string;
  /** Degrees per tap of the rotate buttons; 0 hides them. */
  rotateStep: number;
  /** The slot's fit: a `contain` slot starts from its letterboxed picture. */
  fit?: 'cover' | 'contain' | 'fill';
  /** The slot's own mask, so the gesture stand-in shows only what prints. */
  maskUrl?: string;
  /** Customily's black-and-white masks: black shows, white hides. */
  maskMode?: 'alpha' | 'black-white';
  /** The picture is a cut-out (transparent around the subject). */
  cutout?: boolean;
  /** A mirrored layer: the full-screen framer mirrors the photo the same way. */
  flipX?: boolean;
  flipY?: boolean;
}

/** A map slot the shopper can pan and zoom, Customily-style. */
interface MapTarget {
  optionId: string;
  lat: number;
  lng: number;
  zoom: number;
  label?: string;
  /** Slot box in viewport coordinates. */
  rect: { x: number; y: number; w: number; h: number };
  /** Viewport pixels per scene unit — converts a drag into world movement. */
  pxPerScene: number;
  /** Levels the slot draws `zoom` deeper at its size (the map's viewport frame). */
  zoomOffset: number;
}

// Web-Mercator, in world fractions (0–1 across the whole world at any zoom).
// Exported for tests: a sign error here scrolls the map the wrong way and a
// projection error drifts every drag towards the poles.
export const mercX = (lng: number): number => (lng + 180) / 360;
export const mercY = (lat: number): number => {
  const sin = Math.sin((lat * Math.PI) / 180);
  return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
};
export const invMercX = (x: number): number => x * 360 - 180;
export const invMercY = (y: number): number =>
  (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;

/** One drag step: screen delta → new place, at the map's zoom. Pure, tested. */
export function panPlace(
  start: { lat: number; lng: number },
  dxScreen: number,
  dyScreen: number,
  pxPerScene: number,
  zoom: number,
): { lat: number; lng: number } {
  const worldPx = 256 * 2 ** zoom;
  const dxWorld = dxScreen / pxPerScene / worldPx;
  const dyWorld = dyScreen / pxPerScene / worldPx;
  return {
    lng: invMercX(mercX(start.lng) - dxWorld),
    lat: clampLat(invMercY(mercY(start.lat) - dyWorld)),
  };
}

const clampMapZoom = (zoom: number): number => Math.min(19, Math.max(2, zoom));
const clampLat = (lat: number): number => Math.min(85, Math.max(-85, lat));

export class CanvasEditor {
  private readonly overlays = new Map<string, HTMLElement>();
  private readonly mapOverlays = new Map<string, HTMLElement>();
  private readonly moveOverlays = new Map<string, HTMLElement>();
  /** Library-backed slots the shopper may drag onto one another to swap picks. */
  private readonly swapOverlays = new Map<string, HTMLElement>();
  private readonly images = new Map<string, HTMLImageElement>();
  /**
   * Where the canvas sat when the swap slots' boxes were measured.
   *
   * The boxes are viewport rectangles taken during a draw, but the drag that
   * hit-tests against them happens whenever the shopper likes — and the page
   * scrolls in between. Only the swap needs this: every other overlay works in
   * deltas or in box-relative offsets, which a scroll cannot spoil. Without it
   * a shopper who scrolls the product page after the preview settles drags a
   * graphic onto another and nothing happens.
   */
  private swapOrigin: { left: number; top: number } | undefined;

  /**
   * The questions on screen right now (a wizard step), or undefined for all.
   * A photo's tools belong to its own step: on the song step they only sat on
   * a thumbnail-sized poster, over the very code the shopper was choosing.
   */
  private focus: Set<string> | undefined;

  constructor(private readonly options: CanvasEditorOptions) {}

  /** Shows only the overlays of these options; `null` shows every one again. */
  setFocus(optionIds: string[] | null): void {
    this.focus = optionIds ? new Set(optionIds) : undefined;
    for (const map of [this.overlays, this.mapOverlays]) {
      for (const [optionId, overlay] of map) this.applyFocus(overlay, optionId);
    }
  }

  private applyFocus(overlay: HTMLElement, optionId: string): void {
    const elsewhere = Boolean(this.focus && !this.focus.has(optionId));
    overlay.classList.toggle('is-elsewhere', elsewhere);
    // A photo on another step keeps no tools open over this one.
    if (elsewhere && this.selected === overlay) this.selectOverlay(undefined);
  }

  // ── The tools: a floating bar over the picture being edited ───────────────
  //
  // The buttons used to sit in a row above the preview, always, for every
  // photo on screen — a strip of controls pushing the product down even while
  // the shopper was typing a name. Now they are the contextual toolbar every
  // design tool has (Canva, Zazzle, Customily's phone view): tap a photo and it
  // is selected, its tools float just above it (below it, or inside its bottom
  // edge, when there is no room); tap anywhere else and they go. A fresh
  // upload is selected once, so the shopper sees what can be done with it.

  /** The overlay whose tools are showing. */
  private selected: HTMLElement | undefined;
  /** An option to select as soon as its overlay is on screen (a fresh upload). */
  private selectWhenShown: string | undefined;
  private listening = false;
  /** True while the full-screen framer is open over the preview. */
  private framing = false;

  /**
   * Selects this option's photo once it is on the preview — called right after
   * an upload, usually before its overlay exists.
   */
  select(optionId: string): void {
    const overlay = this.overlays.get(optionId) ?? this.mapOverlays.get(optionId);
    if (overlay && !overlay.classList.contains('is-elsewhere')) {
      this.selectWhenShown = undefined;
      this.selectOverlay(overlay);
    } else {
      this.selectWhenShown = optionId;
    }
  }

  /** Puts away any open tools. */
  deselect(): void {
    this.selectOverlay(undefined);
  }

  /** The tools of one overlay: a floating bar, hidden until it is selected. */
  private toolGroup(overlay: HTMLElement, optionId: string, tools: HTMLElement): void {
    const pop = document.createElement('div');
    pop.className = 'pz-edit-pop';
    pop.setAttribute('role', 'toolbar');
    pop.setAttribute('aria-label', this.options.labelFor?.(optionId) || 'Podešavanje slike');
    pop.hidden = true;
    pop.appendChild(tools);
    (overlay as ToolOverlay).pzPop = pop;
    this.options.box.appendChild(pop);
  }

  private selectOverlay(overlay: HTMLElement | undefined): void {
    const before = this.selected;
    if (before && before !== overlay) {
      before.classList.remove('is-selected');
      const pop = (before as ToolOverlay).pzPop;
      if (pop) pop.hidden = true;
    }
    this.selected = overlay;
    if (!overlay) return;
    overlay.classList.add('is-selected');
    const pop = (overlay as ToolOverlay).pzPop;
    if (pop) pop.hidden = false;
    this.listenOutside();
    this.positionPop();
  }

  /**
   * Floats the selected overlay's tools just above it, centred on it and kept
   * inside the preview; below it when it is too close to the top, and inside
   * its bottom edge when the photo fills the preview.
   */
  private positionPop(): void {
    const overlay = this.selected;
    const pop = (overlay as ToolOverlay | undefined)?.pzPop;
    if (!overlay || !pop || pop.hidden || typeof overlay.getBoundingClientRect !== 'function') return;
    const parent = (pop.offsetParent as HTMLElement | null) ?? this.options.box;
    const edges = (r: { left: number; top: number; width: number; height: number }) => ({
      left: r.left, top: r.top, width: r.width, height: r.height, right: r.left + r.width, bottom: r.top + r.height,
    });
    const area = edges(parent.getBoundingClientRect());
    const slot = edges(overlay.getBoundingClientRect());
    // A phone-sized preview: icons only, tighter, rather than tools cut off.
    pop.classList.remove('is-compact');
    if ((pop.scrollWidth || 0) > area.width - 12) pop.classList.add('is-compact');
    const width = pop.offsetWidth || 0;
    const height = pop.offsetHeight || 0;
    const gap = 8;
    let top: number;
    let place: 'above' | 'below' | 'inside';
    if (slot.top - gap - height >= area.top + 4) {
      top = slot.top - gap - height;
      place = 'above';
    } else if (slot.bottom + gap + height <= area.bottom - 4) {
      top = slot.bottom + gap;
      place = 'below';
    } else {
      top = Math.max(area.top + 4, Math.min(slot.bottom, area.bottom) - gap - height);
      place = 'inside';
    }
    const centre = slot.left + slot.width / 2;
    const left = Math.max(area.left + 6, Math.min(area.right - 6 - width, centre - width / 2));
    pop.style.left = `${left - area.left - parent.clientLeft}px`;
    pop.style.top = `${top - area.top - parent.clientTop}px`;
    pop.dataset.place = place;
  }

  /** A tap outside the selected photo and its tools puts the tools away. */
  private listenOutside(): void {
    if (this.listening || typeof document.addEventListener !== 'function') return;
    this.listening = true;
    document.addEventListener(
      'pointerdown',
      (event) => {
        const overlay = this.selected as OverlayElement | undefined;
        if (!overlay || this.framing) return;
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        const pop = (overlay as ToolOverlay).pzPop;
        if (path.includes(overlay) || (pop && path.includes(pop))) return;
        // A second finger landing beside a small photo while the first is on
        // it joins the pinch, instead of closing the tools.
        if (overlay.pzFingers?.() && path.includes(this.options.box)) {
          overlay.pzJoin?.(event);
          return;
        }
        // Another photo's own pointerdown selects that one.
        if (path.some((node) => (node as HTMLElement).classList?.contains?.('pz-edit'))) return;
        this.selectOverlay(undefined);
      },
      true,
    );
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.selected && !this.framing) this.selectOverlay(undefined);
    });
  }

  /** Drops an overlay and its tools. */
  private drop(overlay: HTMLElement): void {
    if (this.selected === overlay) this.selectOverlay(undefined);
    (overlay as ToolOverlay).pzPop?.remove();
    overlay.remove();
  }

  /** Drops every overlay — the product was swapped for another template. */
  clear(): void {
    for (const map of [this.overlays, this.mapOverlays, this.moveOverlays, this.swapOverlays]) {
      for (const overlay of map.values()) this.drop(overlay);
      map.clear();
    }
    this.selectOverlay(undefined);
    this.last = undefined;
    this.swapOrigin = undefined;
  }

  /**
   * Reconciles overlays with the current scene and answers.
   *
   * Called after every preview draw: element boxes move only when the canvas
   * resizes, but values appear and disappear as the shopper works.
   */
  update(scene: Scene, bindings: BindingDef[], values: Values): void {
    // Frames are computed in viewport coordinates from where the canvas
    // actually renders — the canvas is centred on the mobile sheet, glued to
    // the corner on desktop, and its box's CSS positioning differs between the
    // two. place() converts to whatever element the overlay really positions
    // against, so no layout variant can shift the dashes off the photo.
    const canvasRect = this.options.canvas.getBoundingClientRect();
    if (!canvasRect.width || !scene.width) return;
    const offsetX = canvasRect.left;
    const offsetY = canvasRect.top;
    const scale = canvasRect.width / scene.width;
    // What the swap's hit-test measures its stored boxes against.
    this.swapOrigin = { left: canvasRect.left, top: canvasRect.top };

    const seen = new Set<string>();
    for (const binding of bindings) {
      if (binding.property !== 'image') continue;
      // A choice-scoped binding places the author's picture for one choice;
      // there is no photo of the shopper's in it to frame.
      if (binding.optionValueId) continue;
      if (this.options.isPhoto && !this.options.isPhoto(binding.optionId)) continue;
      const raw = values[binding.optionId];
      const parsed = parseValue(raw);
      if (!parsed) continue;
      const element = scene.elements.find((el) => el.id === binding.elementId);
      if (!element?.transform) continue;
      // A slot the author opened for library swapping gets the swap overlay
      // instead: two overlays on one layer would fight over the same drag.
      if (librarySwapGroup(element)) continue;

      const { cx, cy, w, h } = element.transform;
      const linear = elementLinear(element.transform);
      const cutout = Boolean(
        element.type === 'image'
        && element.effects?.some((e) => e.kind === 'faceCutout' || e.kind === 'removeBackground')
        && !element.effects.some((e) => e.kind === 'blurBackground' || e.kind === 'aiStyle'),
      );
      const target: Target = {
        optionId: binding.optionId,
        elementId: element.id,
        assetId: parsed.assetId,
        crop: parsed.crop ?? { cx: 0.5, cy: 0.5, zoom: 1 },
        rect: {
          x: offsetX + (cx - w / 2) * scale,
          y: offsetY + (cy - h / 2) * scale,
          w: w * scale,
          h: h * scale,
        },
        aspect: w / h,
        ...(linear ? { linear } : {}),
        cssTransform: cssTransformOf(element.transform),
        ...(element.transform.flipX ? { flipX: true } : {}),
        ...(element.transform.flipY ? { flipY: true } : {}),
        ...(element.type === 'image' ? standInLook(element) : {}),
        rotateStep: turnStepOf(element, cutout),
        ...(element.type === 'image' && element.fit ? { fit: element.fit } : {}),
        ...(cutout ? { cutout: true } : {}),
        ...(element.mask
          ? {
              maskUrl: this.options.resolveUrl(element.mask.assetId),
              maskMode: element.mask.mode === 'black-white' ? ('black-white' as const) : ('alpha' as const),
            }
          : {}),
      };
      seen.add(binding.optionId);
      this.place(target);
    }

    // Slots whose photo was removed lose their overlay.
    for (const [optionId, overlay] of this.overlays) {
      if (!seen.has(optionId)) {
        this.drop(overlay);
        this.overlays.delete(optionId);
      }
    }

    // Map slots: the shopper pans and zooms the map on the preview itself.
    // Off by default — the place is picked in the form's dialog, the way
    // Customily does it — and built only for an option whose author asked for
    // it (`previewPan`), where the author's initial place is the starting point
    // and the first drag already IS a location answer.
    const seenMaps = new Set<string>();
    for (const binding of bindings) {
      // Migrated Customily sets bind the map through 'image'; new ones through
      // 'location'. The resolvers accept both, so the editor must too.
      if (binding.property !== 'location' && binding.property !== 'image') continue;
      if (seenMaps.has(binding.optionId)) continue;
      if (!this.options.allowMapPan?.(binding.optionId)) continue;
      const element = scene.elements.find((el) => el.id === binding.elementId);
      if (!element || element.type !== 'map' || !element.transform) continue;
      if (element.visible === false) continue;

      const config = element.config as { lat?: number; lng?: number; zoom?: number };
      const raw = values[binding.optionId] as
        | { lat?: unknown; lng?: unknown; zoom?: unknown; label?: unknown }
        | undefined;
      // The author's initial location, or the shared world-view default —
      // the same fallback the renderer and the studio use.
      const home = mapLocationOf(element);
      const lat = Number(raw?.lat ?? config.lat ?? home.lat);
      const lng = Number(raw?.lng ?? config.lng ?? home.lng);
      const zoom = clampMapZoom(Number(raw?.zoom ?? config.zoom ?? home.zoom));
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

      const { cx, cy, w, h } = element.transform;
      const target: MapTarget = {
        optionId: binding.optionId,
        lat,
        lng,
        zoom,
        label: typeof raw?.label === 'string' ? raw.label : undefined,
        rect: {
          x: offsetX + (cx - w / 2) * scale,
          y: offsetY + (cy - h / 2) * scale,
          w: w * scale,
          h: h * scale,
        },
        pxPerScene: scale,
        zoomOffset: mapZoomOffset(mapViewportOf(element.config), w, h),
      };
      seenMaps.add(binding.optionId);
      this.placeMap(target);
    }
    for (const [optionId, overlay] of this.mapOverlays) {
      if (!seenMaps.has(optionId)) {
        this.drop(overlay);
        this.mapOverlays.delete(optionId);
      }
    }

    // Movable layers — Customily's "movable text": the author opened the
    // layer up (`shopper.move`), so the shopper drags it about on the preview.
    // The scene arrives with placements already applied, so the overlay sits
    // exactly where the layer is drawn.
    const placements = readPlacements(values);
    const seenMoves = new Set<string>();
    for (const element of scene.elements) {
      if (element.type !== 'text' || element.visible === false) continue;
      if (!element.shopper?.move && !element.shopper?.scale) continue;
      if (!element.transform) continue;
      const { cx, cy, w, h } = element.transform;
      seenMoves.add(element.id);
      const current = placements[element.id];
      this.placeMove({
        elementId: element.id,
        offset: { dx: current?.dx ?? 0, dy: current?.dy ?? 0, scale: current?.scale ?? 1 },
        canMove: Boolean(element.shopper?.move),
        canScale: Boolean(element.shopper?.scale),
        rect: {
          x: offsetX + (cx - w / 2) * scale,
          y: offsetY + (cy - h / 2) * scale,
          w: w * scale,
          h: h * scale,
        },
        placements,
      });
    }
    for (const [elementId, overlay] of this.moveOverlays) {
      if (!seenMoves.has(elementId)) {
        overlay.remove();
        this.moveOverlays.delete(elementId);
      }
    }

    this.updateSwaps(scene, bindings, values, { offsetX, offsetY, scale });

    this.last = { scene, bindings, values };
    // A fresh upload's photo is selected the first time it is on screen.
    const wanted = this.selectWhenShown;
    const ready = wanted ? (this.overlays.get(wanted) ?? this.mapOverlays.get(wanted)) : undefined;
    if (ready && !ready.classList.contains('is-elsewhere')) {
      this.selectWhenShown = undefined;
      this.selectOverlay(ready);
    }
    this.positionPop();
  }

  /** The last reconciled state, to re-place overlays when the bar moves the picture. */
  private last: { scene: Scene; bindings: BindingDef[]; values: Values } | undefined;

  /**
   * Customily's "Allow swapping positions with other library images".
   *
   * Two graphics picked from the SAME library can trade places: drag one onto
   * the other on the preview and their picks exchange, which is how a shopper
   * reorders a row of clipart without touching the form. Strictly opt-in — the
   * author sets `allowLibrarySwap` on both elements — because an invisible
   * overlay that eats drags on a design nobody meant to be draggable is worse
   * than the feature is good.
   *
   * Positions never move: what is exchanged is which asset each slot shows, so
   * the answer written is simply each option's value, crossed over.
   */
  private updateSwaps(
    scene: Scene,
    bindings: BindingDef[],
    values: Values,
    view: { offsetX: number; offsetY: number; scale: number },
  ): void {
    const seen = new Set<string>();
    for (const element of scene.elements) {
      if (element.visible === false || !element.transform) continue;
      const group = librarySwapGroup(element);
      if (!group) continue;
      // The question that fills this slot; nothing to exchange without one.
      const binding = bindings.find((b) => b.elementId === element.id && b.property === 'image');
      if (!binding) continue;

      const { cx, cy, w, h } = element.transform;
      seen.add(element.id);
      this.placeSwap({
        elementId: element.id,
        optionId: binding.optionId,
        group,
        value: values[binding.optionId],
        rect: {
          x: view.offsetX + (cx - w / 2) * view.scale,
          y: view.offsetY + (cy - h / 2) * view.scale,
          w: w * view.scale,
          h: h * view.scale,
        },
      });
    }
    for (const [elementId, overlay] of this.swapOverlays) {
      if (!seen.has(elementId)) {
        overlay.remove();
        this.swapOverlays.delete(elementId);
      }
    }
  }

  private placeSwap(target: SwapTarget): void {
    let overlay = this.swapOverlays.get(target.elementId);
    if (!overlay) {
      overlay = this.buildSwap();
      this.swapOverlays.set(target.elementId, overlay);
      this.options.box.appendChild(overlay);
    }
    const el = overlay as SwapOverlayElement;
    el.pzSwapTarget = el.pzGesture ? { ...(el.pzSwapTarget ?? target), rect: target.rect } : target;
    const parent = (overlay.offsetParent as HTMLElement | null) ?? this.options.box;
    const parentRect = parent.getBoundingClientRect();
    overlay.style.left = `${target.rect.x - parentRect.left - parent.clientLeft}px`;
    overlay.style.top = `${target.rect.y - parentRect.top - parent.clientTop}px`;
    overlay.style.width = `${target.rect.w}px`;
    overlay.style.height = `${target.rect.h}px`;
    if (!el.pzGesture) overlay.style.transform = '';
  }

  /** The swappable slot the pointer is over, other than the one being dragged. */
  private swapUnder(from: SwapTarget, clientX: number, clientY: number): SwapTarget | undefined {
    // The pointer is live and the boxes are from the last draw, so the pointer
    // is carried back into the coordinates they were measured in — the canvas
    // is the fixed thing both are expressed against.
    const live = this.options.canvas.getBoundingClientRect();
    const x = clientX - (live.left - (this.swapOrigin?.left ?? live.left));
    const y = clientY - (live.top - (this.swapOrigin?.top ?? live.top));
    for (const overlay of this.swapOverlays.values()) {
      const other = (overlay as SwapOverlayElement).pzSwapTarget;
      if (!other || other.elementId === from.elementId) continue;
      // Same library only: exchanging a heart for a map pin would put an asset
      // into a slot whose own gallery has never heard of it.
      if (other.group !== from.group) continue;
      const { x: rx, y: ry, w, h } = other.rect;
      if (x >= rx && x <= rx + w && y >= ry && y <= ry + h) return other;
    }
    return undefined;
  }

  private buildSwap(): HTMLElement {
    const overlay = document.createElement('div') as SwapOverlayElement;
    overlay.className = 'pz-edit pz-edit-swap';
    const hint = document.createElement('span');
    hint.className = 'pz-edit-tip';
    hint.textContent = 'Prevuci na drugu sliku da zameniš mesta';
    overlay.append(hint);

    let start: { x: number; y: number } | undefined;
    const paintDrop = (over: SwapTarget | undefined): void => {
      for (const [elementId, other] of this.swapOverlays) {
        other.classList.toggle('is-drop', over?.elementId === elementId);
      }
    };
    overlay.addEventListener('pointerdown', (event) => {
      if (!overlay.pzSwapTarget) return;
      event.preventDefault();
      try {
        overlay.setPointerCapture(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
      start = { x: event.clientX, y: event.clientY };
      this.beginGesture(overlay);
      overlay.classList.add('is-active');
    });
    overlay.addEventListener('pointermove', (event) => {
      if (!start || !overlay.pzSwapTarget) return;
      overlay.style.transform = `translate(${event.clientX - start.x}px, ${event.clientY - start.y}px)`;
      paintDrop(this.swapUnder(overlay.pzSwapTarget, event.clientX, event.clientY));
    });
    const release = (event: PointerEvent): void => {
      if (!start) return;
      start = undefined;
      overlay.pzGesture = false;
      overlay.classList.remove('is-active');
      overlay.style.transform = '';
      paintDrop(undefined);
      const from = overlay.pzSwapTarget;
      if (!from || event.type === 'pointercancel') return;
      const onto = this.swapUnder(from, event.clientX, event.clientY);
      if (!onto) return;
      // Both answers, or neither: a half-applied swap would duplicate one
      // graphic and lose the other.
      this.options.onCrop(from.optionId, onto.value);
      this.options.onCrop(onto.optionId, from.value);
      const carried = from.value;
      from.value = onto.value;
      onto.value = carried;
    };
    overlay.addEventListener('pointerup', release);
    overlay.addEventListener('pointercancel', release);
    tuneForTouch(overlay);
    return overlay;
  }

  private placeMove(target: MoveTarget): void {
    let overlay = this.moveOverlays.get(target.elementId);
    if (!overlay) {
      overlay = this.buildMove();
      this.moveOverlays.set(target.elementId, overlay);
      this.options.box.appendChild(overlay);
    }
    const el = overlay as MoveOverlayElement;
    if (!el.pzGesture) el.pzMoveTarget = target;
    else el.pzMoveTarget = { ...(el.pzMoveTarget ?? target), rect: target.rect };
    overlay.classList.toggle('can-move', target.canMove);
    overlay.classList.toggle('can-scale', target.canScale);
    const parent = (overlay.offsetParent as HTMLElement | null) ?? this.options.box;
    const parentRect = parent.getBoundingClientRect();
    overlay.style.left = `${target.rect.x - parentRect.left - parent.clientLeft}px`;
    overlay.style.top = `${target.rect.y - parentRect.top - parent.clientTop}px`;
    overlay.style.width = `${target.rect.w}px`;
    overlay.style.height = `${target.rect.h}px`;
    if (!el.pzGesture) overlay.style.transform = '';
  }

  /** Writes a movable layer's placement into the form; a default placement is dropped. */
  private reportMove(overlay: MoveOverlayElement): void {
    const target = overlay.pzMoveTarget;
    if (!target) return;
    const entry = {
      dx: round3(target.offset.dx),
      dy: round3(target.offset.dy),
      ...(Math.abs(target.offset.scale - 1) > 0.005 ? { scale: round3(target.offset.scale) } : {}),
    };
    const next = { ...target.placements, [target.elementId]: entry };
    if (Math.abs(entry.dx) < 0.002 && Math.abs(entry.dy) < 0.002 && entry.scale === undefined) {
      delete next[target.elementId];
    }
    this.options.onPlace?.(next);
  }

  private buildMove(): HTMLElement {
    const overlay = document.createElement('div') as MoveOverlayElement;
    overlay.className = 'pz-edit pz-edit-move';
    const hint = document.createElement('span');
    hint.className = 'pz-edit-tip';
    hint.textContent = 'Prevuci da pomeriš';
    // A corner grip resizes, when the author allowed it; the box keeps its
    // proportions and the text re-fits inside the new size.
    const grip = document.createElement('span');
    grip.className = 'pz-edit-grip';
    grip.title = 'Povuci da promeniš veličinu';
    overlay.append(hint, grip);

    let start:
      | { kind: 'move'; x: number; y: number; dx: number; dy: number }
      | { kind: 'scale'; x: number; y: number; scale: number; w: number; h: number }
      | undefined;
    overlay.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      try {
        overlay.setPointerCapture(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
      const target = overlay.pzMoveTarget;
      if (!target) return;
      const onGrip = (event.target as HTMLElement).closest('.pz-edit-grip') !== null;
      if (onGrip && target.canScale) {
        start = { kind: 'scale', x: event.clientX, y: event.clientY, scale: target.offset.scale, w: target.rect.w, h: target.rect.h };
      } else if (target.canMove) {
        start = { kind: 'move', x: event.clientX, y: event.clientY, dx: target.offset.dx, dy: target.offset.dy };
      } else {
        return;
      }
      this.beginGesture(overlay);
      overlay.classList.add('is-active');
    });
    overlay.addEventListener('pointermove', (event) => {
      const target = overlay.pzMoveTarget;
      if (!start || !target) return;
      const movedX = event.clientX - start.x;
      const movedY = event.clientY - start.y;
      if (start.kind === 'move') {
        // The layer follows the finger as a CSS translate; the render lands once.
        overlay.style.transform = `translate(${movedX}px, ${movedY}px)`;
        target.offset = {
          ...target.offset,
          dx: clampOffset(start.dx + movedX / target.rect.w),
          dy: clampOffset(start.dy + movedY / target.rect.h),
        };
        return;
      }
      // Dragging the corner away from the centre grows the box; the current
      // on-screen box is `rect` (already at the old scale), so the factor is
      // relative to it and multiplied back onto the stored scale.
      const factor = Math.max(0.2, 1 + (movedX + movedY) / (start.w + start.h));
      const scale = Math.min(4, Math.max(0.25, start.scale * factor));
      const grow = scale / start.scale;
      overlay.style.transform = `translate(${(start.w * (grow - 1)) / 2}px, ${(start.h * (grow - 1)) / 2}px) scale(${grow})`;
      target.offset = { ...target.offset, scale };
    });
    const release = (): void => {
      if (!start) return;
      start = undefined;
      overlay.classList.remove('is-active');
      overlay.pzGesture = false;
      this.reportMove(overlay);
    };
    overlay.addEventListener('pointerup', release);
    overlay.addEventListener('pointercancel', release);
    tuneForTouch(overlay);
    return overlay;
  }

  private placeMap(target: MapTarget): void {
    let overlay = this.mapOverlays.get(target.optionId);
    if (!overlay) {
      overlay = this.buildMap(target.optionId);
      this.mapOverlays.set(target.optionId, overlay);
      this.options.box.appendChild(overlay);
    }
    this.applyFocus(overlay, target.optionId);
    // Mid-gesture, the pointer handlers own the state; a redraw arriving with
    // the PREVIOUS answer must not yank the map back under the finger.
    if (!(overlay as MapOverlayElement).pzGesture) {
      (overlay as MapOverlayElement).pzMapTarget = target;
    } else {
      (overlay as MapOverlayElement).pzMapTarget = {
        ...((overlay as MapOverlayElement).pzMapTarget ?? target),
        rect: target.rect,
        pxPerScene: target.pxPerScene,
      };
    }
    const parent = (overlay.offsetParent as HTMLElement | null) ?? this.options.box;
    const parentRect = parent.getBoundingClientRect();
    overlay.style.left = `${target.rect.x - parentRect.left - parent.clientLeft}px`;
    overlay.style.top = `${target.rect.y - parentRect.top - parent.clientTop}px`;
    overlay.style.width = `${target.rect.w}px`;
    overlay.style.height = `${target.rect.h}px`;
  }

  /** Writes the map's current place into the form as the location answer. */
  private reportMap(overlay: MapOverlayElement): void {
    const target = overlay.pzMapTarget;
    if (!target) return;
    this.options.onCrop(target.optionId, {
      lat: round5(clampLat(target.lat)),
      lng: round5(target.lng),
      zoom: round3(clampMapZoom(target.zoom)),
      ...(target.label ? { label: target.label } : {}),
    });
  }

  private buildMap(optionId: string): HTMLElement {
    const overlay = document.createElement('div') as MapOverlayElement;
    overlay.className = 'pz-edit pz-edit-map';

    const tools = document.createElement('div');
    tools.className = 'pz-edit-tools';
    const zoomOut = toolButton(TOOL_ICONS.minus, 'Umanji mapu');
    const zoomIn = toolButton(TOOL_ICONS.plus, 'Uvećaj mapu');
    tools.append(zoomOut, zoomIn);

    this.toolGroup(overlay, optionId, tools);

    // A drag re-renders live, throttled: warm renders are a few ms, and tiles
    // for neighbouring ground come out of the browser's HTTP cache.
    let lastLive = 0;
    const liveSoon = (): void => {
      const now = Date.now();
      if (now - lastLive < 140) return;
      lastLive = now;
      this.reportMap(overlay);
    };
    let settleTimer: number | undefined;
    const settleSoon = (): void => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        overlay.pzGesture = false;
        this.reportMap(overlay);
      }, 250);
    };

    const zoomBy = (delta: number): void => {
      const target = overlay.pzMapTarget;
      if (!target) return;
      target.zoom = clampMapZoom(target.zoom + delta);
      this.beginGesture(overlay);
      liveSoon();
      settleSoon();
    };
    zoomIn.addEventListener('click', () => zoomBy(0.5));
    zoomOut.addEventListener('click', () => zoomBy(-0.5));

    const pointers = new Map<number, { x: number; y: number }>();
    let pinchStart: { dist: number; zoom: number } | undefined;
    let panStart: { x: number; y: number; lat: number; lng: number } | undefined;

    overlay.addEventListener('pointerdown', (event) => {
      if ((event.target as HTMLElement).closest('.pz-edit-tools')) return;
      event.preventDefault();
      try {
        overlay.setPointerCapture(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.selectOverlay(overlay);
      const target = overlay.pzMapTarget;
      if (!target) return;
      if (pointers.size === 2) {
        pinchStart = { dist: pinchDistance(pointers), zoom: target.zoom };
        panStart = undefined;
      } else if (pointers.size === 1) {
        panStart = { x: event.clientX, y: event.clientY, lat: target.lat, lng: target.lng };
      }
      overlay.classList.add('is-active');
    });

    overlay.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const target = overlay.pzMapTarget;
      if (!target) return;

      if (pointers.size >= 2 && pinchStart) {
        if (pinchStart.dist > 0) {
          const ratio = pinchDistance(pointers) / pinchStart.dist;
          target.zoom = clampMapZoom(pinchStart.zoom + Math.log2(Math.max(ratio, 1e-6)));
          this.beginGesture(overlay);
          liveSoon();
          settleSoon();
        }
        return;
      }

      if (panStart) {
        // One scene unit is one world-Mercator pixel at the zoom the slot is
        // DRAWN at (that is the renderer's contract), so a screen delta converts
        // to a world-fraction delta through the world's pixel size at that zoom.
        const moved = panPlace(
          panStart,
          event.clientX - panStart.x,
          event.clientY - panStart.y,
          target.pxPerScene,
          target.zoom + target.zoomOffset,
        );
        target.lat = moved.lat;
        target.lng = moved.lng;
        this.beginGesture(overlay);
        liveSoon();
        settleSoon();
      }
    });

    const release = (event: PointerEvent): void => {
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinchStart = undefined;
      if (pointers.size === 0) {
        panStart = undefined;
        overlay.classList.remove('is-active');
        if (overlay.pzGesture) {
          overlay.pzGesture = false;
          window.clearTimeout(settleTimer);
          this.reportMap(overlay);
        }
      }
    };
    overlay.addEventListener('pointerup', release);
    overlay.addEventListener('pointercancel', release);

    overlay.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        zoomBy(-event.deltaY * 0.0022);
      },
      { passive: false },
    );

    tuneForTouch(overlay);
    return overlay;
  }

  private place(target: Target): void {
    let overlay = this.overlays.get(target.optionId);
    if (!overlay) {
      overlay = this.build(target.optionId);
      this.overlays.set(target.optionId, overlay);
      this.options.box.appendChild(overlay);
    }
    const el = overlay as OverlayElement;
    // State the pointer handlers read; refreshed on every draw so an outside
    // change (a new upload, a removed photo) never leaves them stale. Mid-
    // gesture the handlers own the framing: a draw landing with the PREVIOUS
    // answer (the redraw that hides a cut-out's old head, say) used to put the
    // old crop back under the finger, and the next wheel step or button press
    // zoomed from there — the picture jumped.
    const working = el.pzGesture && el.pzTarget?.assetId === target.assetId ? el.pzTarget.crop : undefined;
    el.pzTarget = working ? { ...target, crop: working } : target;
    overlay.classList.toggle('can-rotate', target.rotateStep > 0);
    (overlay as ToolOverlay).pzPop?.classList.toggle('can-rotate', target.rotateStep > 0);
    // "Vrati" goes back to the framing the photo arrived with — the author's
    // starting crop, or a placed Mini Me head — not to a plain centred fit.
    if (!el.pzGesture) {
      if (el.pzBase?.assetId !== target.assetId) {
        el.pzBase = { assetId: target.assetId, crop: { ...target.crop } };
        el.pzTouched = false;
      } else if (!el.pzTouched) {
        el.pzBase.crop = { ...target.crop };
      }
    }
    el.pzSyncTools?.();
    this.applyFocus(overlay, target.optionId);
    const clip = el.pzClip as (HTMLElement & { pzMask?: string }) | undefined;
    if (clip) {
      const key = target.maskUrl ? `${target.maskMode ?? 'alpha'}|${target.maskUrl}` : '';
      if (clip.pzMask !== key) {
        clip.pzMask = key;
        const apply = (url: string | undefined): void => {
          if (clip.pzMask !== key) return; // the slot changed meanwhile
          setMask(clip, url);
        };
        if (!target.maskUrl) apply(undefined);
        else if (target.maskMode === 'black-white') void blackWhiteMask(target.maskUrl).then(apply);
        else apply(target.maskUrl);
      }
    }
    // The layer's clip shape (a heart, a circle) cuts the stand-in as it cuts
    // the render — a rectangle of photo used to slide about inside a heart.
    const shape = el.pzShape as (HTMLElement & { pzMask?: string }) | undefined;
    if (shape && shape.pzMask !== (target.shapeMask ?? '')) {
      shape.pzMask = target.shapeMask ?? '';
      setMask(shape, target.shapeMask);
    }
    // Warm the image cache now: the pan handler needs the photo's real size
    // synchronously, mid-gesture, and cannot wait for a load then.
    void this.image(target.assetId);
    // rect is in viewport coordinates; convert into the overlay's actual
    // positioning context. offsetParent is whatever element CSS really made
    // the containing block — measured, never assumed. Absolute positioning is
    // relative to its padding box, hence the clientLeft/clientTop correction.
    const parent = (overlay.offsetParent as HTMLElement | null) ?? this.options.box;
    const parentRect = parent.getBoundingClientRect();
    overlay.style.left = `${target.rect.x - parentRect.left - parent.clientLeft}px`;
    overlay.style.top = `${target.rect.y - parentRect.top - parent.clientTop}px`;
    overlay.style.width = `${target.rect.w}px`;
    overlay.style.height = `${target.rect.h}px`;
    // A tilted, skewed or mirrored layer: the overlay turns with it, so the
    // dashes, the hit area and the stand-in all sit where the photo is drawn.
    overlay.style.transform = target.cssTransform;
    this.settle(el);
    this.scheduleAbove();
  }

  /** The scene the preview last actually painted (not merely requested). */
  private lastPainted: Scene | undefined;

  /**
   * The preview has just painted this scene. Stand-ins whose framing it now
   * shows can go: a draw requested while another was still running resolves
   * at once, so "the draw promise settled" never meant "the canvas shows it",
   * and the old framing flashed for a beat after the finger lifted.
   */
  painted(scene: Scene | undefined): void {
    if (!scene) return;
    this.lastPainted = scene;
    for (const overlay of this.overlays.values()) this.settle(overlay as OverlayElement);
  }

  /** The framing the last painted scene shows for this slot, or undefined when hidden. */
  private paintedKey(target: Target, visibleOnly = true): string | undefined {
    const drawn = this.lastPainted?.elements.find((el) => el.id === target.elementId);
    if (!drawn || drawn.type !== 'image' || drawn.value !== target.assetId) return undefined;
    if (visibleOnly && drawn.visible === false) return undefined;
    return cropKey(drawn.crop);
  }

  /** Retires the live stand-in once the render shows what it showed. */
  private settle(el: OverlayElement): void {
    const live = el.pzLive;
    const target = el.pzTarget;
    if (!live || live.hidden || el.pzGesture || !target) return;
    const pending = el.pzPending;
    let done = !pending || Date.now() >= pending.until;
    if (!done && pending) {
      const key = this.paintedKey(target);
      // A reset to the plain framing reads as the layer's own authored crop,
      // which the editor never sees: any change from the start will do.
      done = key !== undefined && (key === pending.key || (pending.key === DEFAULT_KEY && key !== pending.startKey));
    }
    if (!done) return;
    el.pzPending = undefined;
    live.hidden = true;
    this.hideAbove(el);
  }

  /**
   * Positions the live stand-in so it shows exactly the framing the gesture
   * has reached: the renderer's own placement (`photoPlacement`), including the
   * extra cover a straightened photo needs, expressed as CSS.
   */
  private positionLive(overlay: OverlayElement): void {
    const target = overlay.pzTarget;
    const live = overlay.pzLive;
    if (!target || !live) return;
    const image = this.fresh(target.assetId);
    if (!image || !image.complete || image.width === 0) return;
    this.startFromFit(target, image);

    if (live.src !== image.src) live.src = image.src;
    const box = boxOf(target);
    const place = photoPlacement(sizeOf(image), box, target.crop);
    live.style.width = `${place.w}px`;
    live.style.height = `${place.h}px`;
    live.style.left = `${place.x}px`;
    live.style.top = `${place.y}px`;
    // Only the chosen head of a group cut-out shows, as in the render.
    const keep = target.crop.free ? target.crop.keep : undefined;
    live.style.clipPath = keep
      ? `inset(${keep.y * 100}% ${(1 - keep.x - keep.w) * 100}% ${(1 - keep.y - keep.h) * 100}% ${keep.x * 100}%)`
      : '';
    // Rotation turns about the BOX centre, matching the renderer: express the
    // box centre in the stand-in's own coordinate space as transform-origin.
    if (place.rotation) {
      live.style.transformOrigin = `${box.w / 2 - place.x}px ${box.h / 2 - place.y}px`;
      live.style.transform = `rotate(${place.rotation}deg)`;
    } else {
      live.style.transform = '';
    }
    live.style.filter = target.filter ?? '';
    live.style.opacity = target.opacity != null && target.opacity < 1 ? String(target.opacity) : '';
    live.hidden = false;
  }

  /**
   * A viewport point in the slot's own frame: box units from its top-left
   * corner, before the layer's turn, skew and flips — the frame the crop
   * maths works in. The overlay's measured box is centred on the layer's
   * centre whatever its transform, so the page may have scrolled since the
   * last draw without spoiling it.
   */
  private toBox(overlay: OverlayElement, clientX: number, clientY: number): { x: number; y: number } {
    const target = overlay.pzTarget!;
    const r = overlay.getBoundingClientRect();
    const local = unapply(target.linear, clientX - (r.left + r.width / 2), clientY - (r.top + r.height / 2));
    return { x: local.x + target.rect.w / 2, y: local.y + target.rect.h / 2 };
  }

  private build(optionId: string): HTMLElement {
    const overlay = document.createElement('div') as OverlayElement;
    overlay.className = 'pz-edit pz-edit-photo';

    // The gesture-time stand-in: the photo itself, moved with CSS.
    const live = document.createElement('img');
    live.className = 'pz-edit-live';
    live.hidden = true;
    live.draggable = false;
    live.alt = '';
    overlay.pzLive = live;
    // The stand-in sits in a box the size of the slot, masked like the slot
    // (and cut to its clip shape), so a cut-out head dragged about shows
    // exactly what the print will.
    const clip = document.createElement('span');
    clip.className = 'pz-edit-clip';
    const shape = document.createElement('span');
    shape.className = 'pz-edit-clip';
    shape.appendChild(live);
    clip.appendChild(shape);
    overlay.pzClip = clip;
    overlay.pzShape = shape;

    const tools = document.createElement('div');
    tools.className = 'pz-edit-tools';
    const zoomOut = toolButton(TOOL_ICONS.minus, 'Umanji');
    const zoomIn = toolButton(TOOL_ICONS.plus, 'Uvećaj');
    // Turning the picture inside its box — by the author's step, so a "90°
    // lock" product only ever offers quarter turns.
    const turnLeft = toolButton(TOOL_ICONS.turnLeft, 'Okreni ulevo');
    const turnRight = toolButton(TOOL_ICONS.turnRight, 'Okreni udesno');
    turnLeft.classList.add('pz-edit-turn');
    turnRight.classList.add('pz-edit-turn');
    const reset = toolButton(TOOL_ICONS.reset, 'Vrati kako je bilo');
    // The photo on a screen of its own: a small slot on a phone cannot be
    // pinched with any precision where it sits.
    const expand = toolButton(TOOL_ICONS.expand, 'Uredi sliku preko celog ekrana', 'Uredi');
    expand.classList.add('pz-edit-expand');
    const divider = (): HTMLElement => {
      const line = document.createElement('span');
      line.className = 'pz-edit-sep';
      line.setAttribute('aria-hidden', 'true');
      return line;
    };
    const turnSep = divider();
    turnSep.classList.add('pz-edit-turn');
    tools.append(zoomOut, zoomIn, turnSep, turnLeft, turnRight, divider(), reset, expand);

    overlay.append(clip);
    this.toolGroup(overlay, optionId, tools);
    overlay.pzSyncTools = () => {
      reset.disabled = !overlay.pzTouched;
    };

    // Pointer state: one finger pans, two pinch-zoom (about their midpoint,
    // and slide together), a mouse wheel zooms about the pointer.
    const pointers = new Map<number, { x: number; y: number }>();
    let pinchStart: { dist: number; crop: Crop; mid: { x: number; y: number } } | undefined;
    let panStart: { x: number; y: number; crop: Crop } | undefined;

    // Discrete changes (buttons, wheel) show instantly through the stand-in
    // and collapse into one render once the shopper pauses.
    let settleTimer: number | undefined;
    const settleSoon = (): void => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        if (pointers.size) return; // fingers still down: their release reports
        overlay.pzGesture = false;
        void this.report(overlay);
      }, 250);
    };

    // The framing a run of turns started from, and the framing the last turn
    // left: any other change in between (a drag, a zoom) starts a new run.
    let turnRun: { from: Crop; key: string } | undefined;
    const turnBy = (direction: -1 | 1): void => {
      const target = overlay.pzTarget;
      if (!target || target.rotateStep <= 0) return;
      this.startFromFit(target);
      const step = target.rotateStep;
      // Snap to the step grid, so a free-rotated photo lands on a clean angle.
      const current = target.crop.rotation ?? 0;
      let next = Math.round(current / step) * step + direction * step;
      next = ((next + 180) % 360 + 360) % 360 - 180;
      const image = this.fresh(target.assetId);
      if (image?.width) {
        // The picture keeps its size through the turn: `zoom` is relative to
        // the TURNED box's cover, so leaving it as it was grew the photo on
        // every tap, about the box centre — a photo framed off-centre slid
        // sideways, and a cut-out head grew for no reason at all.
        if (!turnRun || turnRun.key !== cropKey(target.crop)) turnRun = { from: this.settled(target), key: '' };
        target.crop = keepFlags(target.crop, turnPhotoCrop(sizeOf(image), boxOf(target), turnRun.from, next));
        turnRun.key = cropKey(target.crop);
      } else {
        const { rotation: _old, ...rest } = target.crop;
        void _old;
        target.crop = next === 0 ? rest : { ...rest, rotation: next };
      }
      this.beginGesture(overlay);
      this.positionLive(overlay);
      settleSoon();
    };
    // Held, the small cut-out step keeps turning.
    repeatWhileHeld(turnLeft, () => turnBy(-1));
    repeatWhileHeld(turnRight, () => turnBy(1));

    reset.addEventListener('click', () => {
      const target = overlay.pzTarget;
      const base = overlay.pzBase;
      if (!target || !base || base.assetId !== target.assetId) return;
      target.crop = { ...base.crop };
      turnRun = undefined;
      overlay.pzResetting = true;
      this.beginGesture(overlay);
      this.positionLive(overlay);
      settleSoon();
    });
    expand.addEventListener('click', () => void this.openFramer(overlay));
    // A double tap (or double click) on the photo opens it large as well.
    overlay.addEventListener('dblclick', (event) => {
      event.preventDefault();
      void this.openFramer(overlay);
    });

    /** Zooms to `zoom`, holding the picture under `at` (box units) in place. */
    const zoomTo = (zoom: number, at?: { x: number; y: number }): void => {
      const target = overlay.pzTarget;
      if (!target) return;
      this.startFromFit(target);
      const image = this.fresh(target.assetId);
      const next = clampZoom(zoom, target.crop.free);
      const box = boxOf(target);
      target.crop = image?.width
        ? keepFlags(target.crop, zoomPhotoCropAt(sizeOf(image), box, target.crop, next, at ?? { x: box.w / 2, y: box.h / 2 }))
        : { ...target.crop, zoom: next };
      this.beginGesture(overlay);
      this.positionLive(overlay);
      settleSoon();
    };
    // A cut-out head is fine work — a few per cent a tap, not a fifth — and a
    // held button keeps going, so the smaller step costs no extra taps.
    const zoomStep = (target: Target): number => (target.cutout || target.crop.free ? CUTOUT_ZOOM_STEP : PHOTO_ZOOM_STEP);
    repeatWhileHeld(zoomIn, () => {
      const target = overlay.pzTarget;
      if (target) zoomTo(target.crop.zoom * zoomStep(target));
    });
    repeatWhileHeld(zoomOut, () => {
      const target = overlay.pzTarget;
      if (target) zoomTo(target.crop.zoom / zoomStep(target));
    });

    /** (Re)starts whichever gesture the fingers now down make, from where the picture is. */
    const restart = (): void => {
      const target = overlay.pzTarget;
      if (!target) return;
      this.startFromFit(target);
      target.crop = this.settled(target);
      const [a, b] = [...pointers.values()];
      if (a && b) {
        pinchStart = {
          dist: pinchDistance(pointers),
          crop: { ...target.crop },
          mid: this.toBox(overlay, (a.x + b.x) / 2, (a.y + b.y) / 2),
        };
        panStart = undefined;
      } else if (a) {
        panStart = { x: a.x, y: a.y, crop: { ...target.crop } };
        pinchStart = undefined;
      } else {
        panStart = undefined;
        pinchStart = undefined;
      }
    };

    const down = (event: PointerEvent): void => {
      event.preventDefault();
      try {
        overlay.setPointerCapture(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.selectOverlay(overlay);
      if (!overlay.pzTarget) return;
      window.clearTimeout(settleTimer);
      restart();
      overlay.classList.add('is-active');
      overlay.pzPop?.classList.add('is-busy');
    };
    overlay.addEventListener('pointerdown', down);
    overlay.pzFingers = () => pointers.size;
    overlay.pzJoin = down;

    overlay.addEventListener('pointermove', (event) => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const target = overlay.pzTarget;
      if (!target) return;
      const image = this.fresh(target.assetId);
      if (!image || !image.complete || image.width === 0) return;
      const source = sizeOf(image);
      const box = boxOf(target);

      if (pointers.size >= 2 && pinchStart) {
        const [a, b] = [...pointers.values()];
        if (pinchStart.dist > 0 && a && b) {
          const zoom = clampZoom(pinchStart.crop.zoom * (pinchDistance(pointers) / pinchStart.dist), target.crop.free);
          const mid = this.toBox(overlay, (a.x + b.x) / 2, (a.y + b.y) / 2);
          target.crop = keepFlags(pinchStart.crop, zoomPhotoCropAt(source, box, pinchStart.crop, zoom, pinchStart.mid, mid));
          this.beginGesture(overlay);
          this.positionLive(overlay);
        }
        return;
      }

      if (panStart) {
        // The finger's travel in the slot's own frame (a tilted or mirrored
        // slot turns it), then through the photo's own straightening: the
        // picture follows the finger whatever either angle is.
        const moved = unapply(target.linear, event.clientX - panStart.x, event.clientY - panStart.y);
        target.crop = keepFlags(panStart.crop, panPhotoCrop(source, box, panStart.crop, moved.x, moved.y));
        this.beginGesture(overlay);
        this.positionLive(overlay);
      }
    });

    const release = (event: PointerEvent): void => {
      if (!pointers.delete(event.pointerId)) return;
      if (pointers.size > 0) {
        // One finger of a pinch lifted: the other carries on panning from
        // where the picture is now, instead of freezing until it lifts too.
        restart();
        return;
      }
      panStart = undefined;
      pinchStart = undefined;
      overlay.classList.remove('is-active');
      overlay.pzPop?.classList.remove('is-busy');
      // The gesture is over: one render catches the preview up. The stand-in
      // stays visible until that render lands (place() hides it).
      if (overlay.pzGesture) {
        overlay.pzGesture = false;
        void this.report(overlay);
      }
    };
    overlay.addEventListener('pointerup', release);
    overlay.addEventListener('pointercancel', release);
    // A capture lost without an up (the browser took the touch for a scroll,
    // a system sheet opened) must not leave a finger "down" for good.
    overlay.addEventListener('lostpointercapture', release);

    overlay.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const target = overlay.pzTarget;
        if (!target) return;
        const rate = target.cutout || target.crop.free ? 0.0006 : 0.0015;
        zoomTo(target.crop.zoom * Math.exp(-event.deltaY * rate), this.toBox(overlay, event.clientX, event.clientY));
      },
      { passive: false },
    );

    tuneForTouch(overlay);
    return overlay;
  }

  /**
   * Opens the photo on a screen of its own ("Uredi"), and writes what the
   * shopper framed there exactly as a gesture on the preview would be written.
   */
  private async openFramer(overlay: OverlayElement): Promise<void> {
    const target = overlay.pzTarget;
    if (!target || this.framing) return;
    this.framing = true;
    try {
      const image = await this.image(target.assetId);
      if (!image?.width) return;
      this.startFromFit(target, image);
      const maskUrl = !target.maskUrl
        ? undefined
        : target.maskMode === 'black-white'
          ? await blackWhiteMask(target.maskUrl)
          : target.maskUrl;
      const framed = await openPhotoFramer({
        host: layerHostOf(this.options.box),
        title: this.options.labelFor?.(target.optionId) || 'Uredi sliku',
        imageUrl: image.src,
        source: sizeOf(image),
        aspect: target.aspect,
        crop: this.settled(target),
        rotateStep: target.rotateStep,
        cutout: Boolean(target.cutout || target.crop.free),
        ...(maskUrl ? { maskUrl } : {}),
        ...(target.shapeMask ? { shapeMask: target.shapeMask } : {}),
        ...(target.filter ? { filter: target.filter } : {}),
        ...(target.flipX ? { flipX: true } : {}),
        ...(target.flipY ? { flipY: true } : {}),
        clampZoom,
      });
      const now = overlay.pzTarget;
      if (!framed || !now || now.assetId !== target.assetId) return;
      now.crop = keepFlags(now.crop, framed);
      this.beginGesture(overlay);
      this.positionLive(overlay);
      overlay.pzGesture = false;
      await this.report(overlay);
    } finally {
      this.framing = false;
    }
  }

  /** The target's crop as the renderer reads it (clamped), once its picture is known. */
  private settled(target: Target): Crop {
    const image = this.fresh(target.assetId);
    if (!image?.width) return target.crop;
    return keepFlags(target.crop, coverPhotoFrame(sizeOf(image), boxOf(target), target.crop).crop);
  }

  /**
   * Marks a gesture under way. The old render of the photo is hidden when the
   * stand-in cannot cover it: a cut-out (transparent round the head) or any
   * picture zoomed out below the slot — two pictures showed otherwise.
   */
  private beginGesture(overlay: OverlayElement): void {
    if (!overlay.pzGesture && overlay.pzTarget) overlay.pzStartKey = this.paintedKey(overlay.pzTarget, false);
    overlay.pzGesture = true;
    const target = overlay.pzTarget;
    if (target && (target.cutout || target.crop.free) && !overlay.pzDragging) {
      overlay.pzDragging = true;
      this.options.onDragging?.(target.optionId, true);
    }
    if (!overlay.pzAbove) this.showAbove(overlay);
  }

  /** Clamps the framing against the real image and writes it into the form. */
  private async report(overlay: OverlayElement): Promise<void> {
    const target = overlay.pzTarget;
    if (!target) return;
    const image = await this.image(target.assetId);
    // Written exactly as the renderer will read it, so what was on screen when
    // the finger lifted is what the print gets (a straightened photo's centre
    // is bounded by its turned window, not the plain one).
    if (image?.width) target.crop = keepFlags(target.crop, coverPhotoFrame(sizeOf(image), boxOf(target), target.crop).crop);
    const free = target.crop.free === true;
    const { cx, cy, zoom, rotation } = target.crop;
    const isDefault =
      !free &&
      Math.abs(zoom - 1) < 0.01 &&
      Math.abs(cx - 0.5) < 0.005 &&
      Math.abs(cy - 0.5) < 0.005 &&
      !rotation;
    const crop: Crop = {
      cx: round3(cx),
      cy: round3(cy),
      zoom: round3(zoom),
      // A straightened photo must survive a later pan — the drag
      // rewrites the whole crop, so the angle rides along.
      ...(rotation ? { rotation: round3(rotation) } : {}),
      // A placed head stays free (and keeps only its own head) after
      // the shopper's adjustments.
      ...(free ? { free: true, ...(target.crop.keep ? { keep: target.crop.keep } : {}) } : {}),
    };
    // What the renderer must have painted before the stand-in may go; a
    // renderer that never paints (it failed, or is still loading) gives up the
    // stand-in after a few seconds rather than leaving it up for good.
    overlay.pzTouched = !overlay.pzResetting;
    overlay.pzResetting = false;
    overlay.pzSyncTools?.();
    overlay.pzPending = { key: cropKey(isDefault ? undefined : crop), startKey: overlay.pzStartKey, until: Date.now() + 4000 };
    window.setTimeout(() => this.settle(overlay), 4050);
    this.options.onCrop(target.optionId, isDefault ? target.assetId : { assetId: target.assetId, crop });
    // Released after the new framing is written, so the head reappears where
    // the shopper let go rather than flashing back to where it was.
    if (overlay.pzDragging) {
      overlay.pzDragging = false;
      this.options.onDragging?.(target.optionId, false);
    }
  }

  // ── The layers drawn over a photo, while it is being moved ────────────────

  /** One picture of the layers above each photo, keyed by what it shows. */
  private readonly above = new Map<string, AboveEntry>();
  private aboveTimer: number | undefined;

  /** The scene of just the layers over this photo's layer; undefined when none. */
  private aboveScene(elementId: string): { scene: Scene; key: string } | undefined {
    const scene = this.last?.scene;
    if (!scene || !this.options.renderLayers) return undefined;
    const ordered = [...scene.elements].sort((a, b) => a.z - b.z);
    const index = ordered.findIndex((el) => el.id === elementId);
    if (index < 0) return undefined;
    const over = ordered.slice(index + 1).filter((el) => el.visible !== false && !el.printOnly);
    if (!over.length) return undefined;
    // A layer that blends with what is under it (knock-out text) cannot be
    // drawn apart from it; the stand-in then simply stays on top.
    if (over.some((el) => (el as { blend?: string }).blend)) return undefined;
    const stage = this.options.canvas.getBoundingClientRect();
    const layers: Scene = { ...scene, background: undefined, elements: over };
    return { scene: layers, key: `${Math.round(stage.width)}|${JSON.stringify(over)}` };
  }

  /** After the preview settles, draws each photo's upper layers ahead of any drag. */
  private scheduleAbove(): void {
    if (!this.options.renderLayers) return;
    window.clearTimeout(this.aboveTimer);
    this.aboveTimer = window.setTimeout(() => {
      for (const overlay of this.overlays.values()) {
        const el = overlay as OverlayElement;
        if (el.pzTarget && !el.pzGesture) void this.prepareAbove(el.pzTarget.elementId);
      }
    }, 900);
  }

  private prepareAbove(elementId: string): Promise<void> {
    const wanted = this.aboveScene(elementId);
    const known = this.above.get(elementId);
    if (!wanted) {
      known?.canvas?.remove();
      this.above.delete(elementId);
      return Promise.resolve();
    }
    if (known?.key === wanted.key) return known.task ?? Promise.resolve();
    // The old picture stays in use until the new one is ready.
    const entry: AboveEntry = { key: wanted.key, canvas: known?.canvas };
    this.above.set(elementId, entry);
    entry.task = (async () => {
      const drawn = await this.options.renderLayers!(wanted.scene).catch(() => undefined);
      if (this.above.get(elementId) !== entry) return;
      if (!drawn) {
        // Nothing to lay over; the stand-in stays on top as it always did.
        entry.canvas?.remove();
        entry.canvas = undefined;
        return;
      }
      const canvas = entry.canvas ?? document.createElement('canvas');
      canvas.className = 'pz-edit-above';
      canvas.width = drawn.width;
      canvas.height = drawn.height;
      const bytes = new Uint8ClampedArray(drawn.pixels.byteLength);
      bytes.set(drawn.pixels);
      canvas.getContext('2d')?.putImageData(new ImageData(bytes, drawn.width, drawn.height), 0, 0);
      if (!entry.canvas) canvas.hidden = true;
      entry.canvas = canvas;
      // Shown straight away if a drag began while it was being drawn.
      for (const overlay of this.overlays.values()) {
        const el = overlay as OverlayElement;
        if (el.pzTarget?.elementId === elementId && el.pzGesture) this.showAbove(el);
      }
    })();
    return entry.task;
  }

  /**
   * Lays the upper layers over the moving stand-in, cut to the slot's own
   * outline: outside the slot the preview already shows them, and drawing
   * them twice would darken anything soft or see-through.
   */
  private showAbove(overlay: OverlayElement): void {
    const target = overlay.pzTarget;
    if (!target || !this.options.renderLayers) return;
    const entry = this.above.get(target.elementId);
    if (!entry?.canvas) {
      void this.prepareAbove(target.elementId);
      return;
    }
    const canvas = entry.canvas;
    const box = this.options.box;
    if (canvas.parentElement !== box) box.appendChild(canvas);
    const stage = this.options.canvas.getBoundingClientRect();
    const parent = (canvas.offsetParent as HTMLElement | null) ?? box;
    const parentRect = parent.getBoundingClientRect();
    canvas.style.left = `${stage.left - parentRect.left - parent.clientLeft}px`;
    canvas.style.top = `${stage.top - parentRect.top - parent.clientTop}px`;
    canvas.style.width = `${stage.width}px`;
    canvas.style.height = `${stage.height}px`;
    // The slot's four corners, turned like the layer, in the canvas's own box.
    const slot = overlay.getBoundingClientRect();
    const cx = slot.left + slot.width / 2 - stage.left;
    const cy = slot.top + slot.height / 2 - stage.top;
    const { w, h } = target.rect;
    const corners = [[-w / 2, -h / 2], [w / 2, -h / 2], [w / 2, h / 2], [-w / 2, h / 2]].map(([x, y]) => {
      const p = applyLinear(target.linear, x!, y!);
      return `${(cx + p.x).toFixed(2)}px ${(cy + p.y).toFixed(2)}px`;
    });
    canvas.style.clipPath = `polygon(${corners.join(', ')})`;
    canvas.hidden = false;
    overlay.pzAbove = canvas;
  }

  private hideAbove(overlay: OverlayElement): void {
    if (overlay.pzAbove) overlay.pzAbove.hidden = true;
    overlay.pzAbove = undefined;
  }

  /**
   * The loaded picture behind an id, only while the id still points at it.
   *
   * The same id is re-pointed at new pixels — the cut-out replacing the
   * upload a few seconds later — and a cache keyed by id alone kept showing
   * the uncut photo, background and all, whenever the shopper zoomed.
   */
  private fresh(assetId: string): HTMLImageElement | undefined {
    const cached = this.images.get(assetId);
    const url = this.options.resolveUrl(assetId);
    if (!cached || !url || cached.dataset.pzUrl !== url) {
      if (url && cached?.dataset.pzUrl !== url) void this.image(assetId);
      return undefined;
    }
    return cached;
  }

  private image(assetId: string): Promise<HTMLImageElement | undefined> {
    const url = this.options.resolveUrl(assetId);
    if (!url) return Promise.resolve(undefined);
    const cached = this.images.get(assetId);
    if (cached && cached.dataset.pzUrl === url) return Promise.resolve(cached);
    const loading = this.loading.get(url);
    if (loading) return loading;
    const task = new Promise<HTMLImageElement | undefined>((resolve) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.dataset.pzUrl = url;
      image.onload = () => {
        // Only if the id still points here; a newer URL may have won meanwhile.
        if (this.options.resolveUrl(assetId) === url) this.images.set(assetId, image);
        resolve(image);
      };
      image.onerror = () => resolve(undefined);
      image.src = url;
    }).finally(() => this.loading.delete(url));
    this.loading.set(url, task);
    return task;
  }

  private readonly loading = new Map<string, Promise<HTMLImageElement | undefined>>();

  /**
   * A `contain` slot draws the whole picture letterboxed, and a crop that is
   * not free is ignored there — so a gesture must start from the letterbox,
   * as a free crop the renderer honours, or the shopper's drag does nothing
   * (and the stand-in jumps to a cover framing the print never had).
   *
   * Sized through the renderer's own framing, so a photo already turned inside
   * a contain slot keeps its size as well.
   */
  private startFromFit(target: Target, loaded?: HTMLImageElement): void {
    if (target.fit !== 'contain' || target.crop.free) return;
    const image = loaded ?? this.fresh(target.assetId);
    if (!image || !image.width || !image.height) return;
    const source = sizeOf(image);
    const box = boxOf(target);
    const contain = Math.min(box.w / source.w, box.h / source.h);
    const rotation = target.crop.rotation;
    const base = coverPhotoFrame(source, box, { cx: 0.5, cy: 0.5, zoom: 1, free: true, ...(rotation ? { rotation } : {}) });
    target.crop = {
      cx: 0.5,
      cy: 0.5,
      zoom: Math.max(FREE_ZOOM_MIN, contain / base.scale),
      ...(rotation ? { rotation } : {}),
      free: true,
    };
  }
}

/** An overlay whose buttons live in the tool bar above the picture. */
type ToolOverlay = HTMLElement & { pzPop?: HTMLElement };

type MapOverlayElement = HTMLElement & {
  pzMapTarget?: MapTarget;
  pzGesture?: boolean;
};

/** A movable layer the shopper can drag (and resize) on the preview. */
interface MoveTarget {
  elementId: string;
  offset: { dx: number; dy: number; scale: number };
  canMove: boolean;
  canScale: boolean;
  /** Layer box in viewport coordinates, placement already applied. */
  rect: { x: number; y: number; w: number; h: number };
  /** Every placement so far, so one drag rewrites the map without losing the rest. */
  placements: Record<string, { dx: number; dy: number; scale?: number }>;
}

type MoveOverlayElement = HTMLElement & {
  pzMoveTarget?: MoveTarget;
  pzGesture?: boolean;
};

/** One library-backed slot the shopper may exchange with another. */
interface SwapTarget {
  elementId: string;
  /** The question that fills this slot; the swap rewrites its answer. */
  optionId: string;
  /** The library both sides must share for the exchange to make sense. */
  group: string;
  /** That question's current answer — the thing actually exchanged. */
  value: unknown;
  /** Slot box in viewport coordinates. */
  rect: { x: number; y: number; w: number; h: number };
}

type SwapOverlayElement = HTMLElement & {
  pzSwapTarget?: SwapTarget;
  pzGesture?: boolean;
};

/**
 * The library an element swaps within, or undefined when it does not swap.
 *
 * Two gates, both required: the author's `allowLibrarySwap` flag, and a source
 * that really is a library — an upload slot or fixed artwork has no gallery to
 * exchange within. Read structurally rather than through the scene types
 * because `allowLibrarySwap` is a new field: an older definition simply has no
 * swappable elements, which is exactly the right answer for it.
 */
export function librarySwapGroup(element: unknown): string | undefined {
  const el = element as {
    allowLibrarySwap?: unknown;
    source?: { kind?: unknown; libraryId?: unknown };
  };
  if (el.allowLibrarySwap !== true) return undefined;
  if (el.source?.kind !== 'library') return undefined;
  return typeof el.source.libraryId === 'string' && el.source.libraryId !== ''
    ? el.source.libraryId
    : undefined;
}

const clampOffset = (v: number): number => Math.min(3, Math.max(-3, v));

type OverlayElement = HTMLElement & {
  pzTarget?: Target;
  /** The gesture-time CSS stand-in for the photo. */
  pzLive?: HTMLImageElement;
  /** The slot-sized, slot-masked box the stand-in is drawn in. */
  pzClip?: HTMLElement;
  /** Inside it, the box cut to the layer's clip shape. */
  pzShape?: HTMLElement;
  /** True while fingers are down or discrete zooms are still settling. */
  pzGesture?: boolean;
  /** The preview was told to hide this cut-out's old render. */
  pzDragging?: boolean;
  /** The framing painted when the gesture began. */
  pzStartKey?: string;
  /** A framing written but not painted yet; the stand-in stays until it is. */
  pzPending?: { key: string; startKey?: string; until: number };
  /** The upper layers laid over the stand-in during this gesture. */
  pzAbove?: HTMLCanvasElement;
  /** The floating tools of this photo. */
  pzPop?: HTMLElement;
  /** The framing the photo arrived with, which "Vrati" goes back to. */
  pzBase?: { assetId: string; crop: Crop };
  /** The shopper has framed this photo by hand since it arrived. */
  pzTouched?: boolean;
  /** The next report is a "Vrati", not a change of the shopper's. */
  pzResetting?: boolean;
  /** Brings the tools' enabled states up to date. */
  pzSyncTools?: () => void;
  /** Fingers down on the photo right now. */
  pzFingers?: () => number;
  /** Takes a finger that landed beside the photo into its gesture. */
  pzJoin?: (event: PointerEvent) => void;
};

/** A drawn picture of the layers over one photo, and what it was drawn from. */
interface AboveEntry {
  key: string;
  canvas?: HTMLCanvasElement;
  task?: Promise<void>;
}

/** The linear part of a layer's transform: CSS `matrix(a, b, c, d, 0, 0)`. */
interface Linear {
  a: number;
  b: number;
  c: number;
  d: number;
}

/**
 * The layer's rotation, skew and flips as one matrix, in the renderer's order
 * (rotate, then skew, then flip, all about the centre — `drawElementFlat`).
 * Undefined for an upright, unmirrored layer: the common case stays exact.
 */
export function elementLinear(t: Pick<Transform, 'rotation' | 'skewX' | 'skewY' | 'flipX' | 'flipY'>): Linear | undefined {
  const rotation = t.rotation ?? 0;
  const kx = Math.tan(((t.skewX ?? 0) * Math.PI) / 180);
  const ky = Math.tan(((t.skewY ?? 0) * Math.PI) / 180);
  const fx = t.flipX ? -1 : 1;
  const fy = t.flipY ? -1 : 1;
  if (!rotation && !kx && !ky && fx === 1 && fy === 1) return undefined;
  const cos = Math.cos((rotation * Math.PI) / 180);
  const sin = Math.sin((rotation * Math.PI) / 180);
  // R·K, then the flip scales its columns.
  return {
    a: (cos - sin * ky) * fx,
    b: (sin + cos * ky) * fx,
    c: (cos * kx - sin) * fy,
    d: (sin * kx + cos) * fy,
  };
}

/** The same transform in CSS, applied about the overlay's centre. */
function cssTransformOf(t: Transform): string {
  const parts: string[] = [];
  if (t.rotation) parts.push(`rotate(${t.rotation}deg)`);
  if (t.skewX || t.skewY) parts.push(`skew(${t.skewX ?? 0}deg, ${t.skewY ?? 0}deg)`);
  if (t.flipX || t.flipY) parts.push(`scale(${t.flipX ? -1 : 1}, ${t.flipY ? -1 : 1})`);
  return parts.join(' ');
}

/** A vector through the layer's transform. */
export function applyLinear(m: Linear | undefined, x: number, y: number): { x: number; y: number } {
  return m ? { x: m.a * x + m.c * y, y: m.b * x + m.d * y } : { x, y };
}

/** A viewport vector carried back into the layer's own frame. */
export function unapply(m: Linear | undefined, x: number, y: number): { x: number; y: number } {
  if (!m) return { x, y };
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < 1e-9) return { x, y };
  return { x: (m.d * x - m.c * y) / det, y: (-m.b * x + m.a * y) / det };
}

const sizeOf = (image: { width: number; height: number }): { w: number; h: number } => ({ w: image.width, h: image.height });
const boxOf = (target: Target): { w: number; h: number } => ({ w: target.rect.w, h: target.rect.h });

/** A recomputed crop keeps the head it was showing (`keep`) and its free framing. */
function keepFlags(from: Crop, next: ImageCrop): Crop {
  const out: Crop = { cx: next.cx, cy: next.cy, zoom: next.zoom };
  if (next.rotation) out.rotation = next.rotation;
  if (from.free) {
    out.free = true;
    if (from.keep) out.keep = from.keep;
  }
  return out;
}

/** The plain framing: no crop on the answer. */
const DEFAULT_KEY = 'default';

/** A framing as a comparable string, to three decimals as it is written. */
function cropKey(crop: { cx: number; cy: number; zoom: number; rotation?: number; free?: boolean } | undefined): string {
  if (!crop) return DEFAULT_KEY;
  return [crop.cx, crop.cy, crop.zoom, crop.rotation ?? 0].map((v) => round3(v)).join('|') + (crop.free ? '|f' : '');
}

/** Sets (or clears) a CSS mask on an element, prefixed for Safari. */
function setMask(element: HTMLElement, url: string | undefined): void {
  const value = url ? `url("${url.replace(/"/g, '%22')}")` : '';
  const style = element.style as CSSStyleDeclaration & { webkitMaskImage?: string };
  style.maskImage = value;
  style.webkitMaskImage = value;
}

/**
 * How the layer's picture looks beyond its framing, for the stand-in: the
 * colour effects CSS can reproduce, its opacity and its clip shape.
 */
function standInLook(element: Extract<SceneElement, { type: 'image' }>): Pick<Target, 'filter' | 'opacity' | 'shapeMask'> {
  const filter = photoCssFilter(element.effects);
  const shape = element.clipShape ? shapeMaskUrl(element.clipShape) : undefined;
  return {
    ...(filter ? { filter } : {}),
    ...(element.opacity != null && element.opacity < 1 ? { opacity: element.opacity } : {}),
    ...(shape ? { shapeMask: shape } : {}),
  };
}

/** A clip shape's unit-box outline as an SVG mask stretched over the box, once per shape. */
const shapeMasks = new Map<string, string>();
function shapeMaskUrl(kind: string): string {
  let url = shapeMasks.get(kind);
  if (!url) {
    const def = shapeDef(kind);
    const rule = def.fillRule ? ` fill-rule="${def.fillRule}"` : '';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100" preserveAspectRatio="none"><path transform="scale(100)" d="${def.d}" fill="#000"${rule}/></svg>`;
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    shapeMasks.set(kind, url);
  }
  return url;
}

/**
 * Makes one overlay usable with a thumb.
 *
 * Two separate problems. The buttons and grips are drawn at 26-30px because
 * that is what looks right over a photo, which is well under the 44px a finger
 * needs; `is-coarse` lets the stylesheet grow the HIT area without growing the
 * drawing. And on iOS Safari a two-finger pinch over the page is the browser's
 * own zoom: without `gesturestart`/`gesturechange` being refused, pinching to
 * zoom the photo zooms the whole shop page instead and the shopper is left
 * looking at a magnified corner of it with their framing unchanged.
 */
function tuneForTouch(overlay: HTMLElement): void {
  if (pointerIsCoarse()) overlay.classList.add('is-coarse');
  const swallow = (event: Event): void => {
    if (event.cancelable) event.preventDefault();
  };
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    overlay.addEventListener(type, swallow, { passive: false });
  }
}

/**
 * The tool glyphs as drawings, not characters: "↺" and "↻" come from whatever
 * font the phone has, and on Android they sat low and small beside − and +.
 */
const toolIcon = (paths: string): string =>
  `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
const TOOL_ICONS = {
  minus: toolIcon('<path d="M6 12h12"/>'),
  plus: toolIcon('<path d="M12 6v12M6 12h12"/>'),
  turnLeft: toolIcon('<path d="M4 5v5h5"/><path d="M5.6 15a7 7 0 1 0 1.4-7.4L4 10"/>'),
  turnRight: toolIcon('<path d="M20 5v5h-5"/><path d="M18.4 15a7 7 0 1 1-1.4-7.4L20 10"/>'),
  reset: toolIcon('<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>'),
  expand: toolIcon('<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>'),
};

/** Zoom per tap of + or −: a photo, and a cut-out head (finer work). */
const PHOTO_ZOOM_STEP = 1.15;
const CUTOUT_ZOOM_STEP = 1.05;
/** Degrees per tap of the turn buttons, unless the author set a step. */
const PHOTO_TURN_STEP = 15;
const CUTOUT_TURN_STEP = 3;

/**
 * Degrees per tap of a slot's turn buttons; 0 hides them. The author's step
 * wins (90 is the rotation lock: quarter turns only). Otherwise a cut-out head
 * turns a few degrees a tap — it is lined up against a drawn body, where 15° is
 * always too much or too little — and a photo 15°.
 */
export function turnStepOf(
  element: { shopper?: { rotate?: boolean; rotateStep?: number } },
  cutout: boolean,
): number {
  if (element.shopper?.rotate === false) return 0;
  return element.shopper?.rotateStep ?? (cutout ? CUTOUT_TURN_STEP : PHOTO_TURN_STEP);
}

function toolButton(icon: string, title: string, text = ''): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'pz-edit-btn';
  button.innerHTML = icon + (text ? `<span>${text}</span>` : '');
  button.title = title;
  button.setAttribute('aria-label', title);
  return button;
}

function parseValue(raw: unknown): { assetId: string; crop?: Crop } | undefined {
  if (typeof raw === 'string' && raw !== '') return { assetId: raw };
  if (raw && typeof raw === 'object' && 'assetId' in raw) {
    const value = raw as { assetId?: unknown; crop?: Crop };
    if (typeof value.assetId === 'string') {
      return { assetId: value.assetId, crop: value.crop ? { ...value.crop } : undefined };
    }
  }
  return undefined;
}

/** Black-and-white masks turned into alpha masks, once per URL. */
const bwMasks = new Map<string, Promise<string | undefined>>();

/**
 * A Customily black-and-white mask as an alpha mask CSS can use.
 *
 * The renderer keeps a pixel where the mask is dark (inverse luminance) AND
 * opaque; CSS masks by alpha only, so the same product is computed once into
 * a small PNG — the stand-in is a screen-sized preview, 512 px is plenty.
 * Undefined (no mask) when the picture cannot be read.
 */
export function blackWhiteMask(url: string): Promise<string | undefined> {
  let known = bwMasks.get(url);
  if (!known) {
    known = new Promise<string | undefined>((resolve) => {
      if (typeof document === 'undefined') return resolve(undefined);
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const k = Math.min(1, 512 / Math.max(image.naturalWidth, image.naturalHeight));
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(image.naturalWidth * k));
          canvas.height = Math.max(1, Math.round(image.naturalHeight * k));
          const context = canvas.getContext('2d');
          if (!context) return resolve(undefined);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
          const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
          const d = pixels.data;
          for (let i = 0; i < d.length; i += 4) {
            const luminance = (0.2126 * d[i]! + 0.7152 * d[i + 1]! + 0.0722 * d[i + 2]!) / 255;
            d[i + 3] = Math.round(d[i + 3]! * (1 - luminance));
            d[i] = d[i + 1] = d[i + 2] = 0;
          }
          context.putImageData(pixels, 0, 0);
          canvas.toBlob((blob) => resolve(blob ? URL.createObjectURL(blob) : undefined), 'image/png');
        } catch {
          resolve(undefined);
        }
      };
      image.onerror = () => resolve(undefined);
      image.src = url;
    });
    bwMasks.set(url, known);
  }
  return known;
}

/** Where a dialog opened from the preview lives: the widget's shadow root. */
function layerHostOf(box: HTMLElement): ShadowRoot | HTMLElement {
  const root = box.getRootNode?.() as (Node & { host?: Element }) | undefined;
  if (root && (root as ShadowRoot).host) return root as ShadowRoot;
  return document.body ?? box;
}

/** A photo zooms 1–4× over its cover fit; a placed head may also shrink (to 0.2×). */
function clampZoom(zoom: number, free = false): number {
  return Math.min(Math.max(zoom, free ? Math.max(FREE_ZOOM_MIN, 0.2) : 1), free ? 8 : 4);
}

function pinchDistance(pointers: Map<number, { x: number; y: number }>): number {
  const [a, b] = [...pointers.values()];
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Five decimals ≈ one metre — plenty for a poster, stable in the answer JSON.
 *
 * Exported because the map dialog in the form writes the same answer shape:
 * one rounding rule, so a place picked in the dialog and the same place dragged
 * on the preview cannot disagree in the last digit.
 */
export function round5(v: number): number {
  return Math.round(v * 100000) / 100000;
}

export function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
